import React, { useCallback, useEffect, useState } from "react";
import { useAuthenticatedApi } from "../../hooks/useAuthenticatedApi";
import { refreshTemplates } from "../../hooks/useTemplates";
import { refreshWorkspaces } from "../ProjectSelectionPage";
import { apiEndpoints } from "../../utils/api";
import { 
  CheckCircle, 
  AlertCircle, 
  ExternalLink,
  Info
} from "lucide-react";
import { useReadOnlyMode } from "../../hooks/useReadOnlyMode";

// Types
interface ExpoIntegrationResponse {
  id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  has_token: boolean;
  expo_username: string | null;
  expo_email: string | null;
  last_validated_at: string | null;
}

interface ExpoIntegrationRequest {
  expo_token: string;
}

interface AlertProps {
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  onDismiss?: () => void;
}

// Constants
const EXPO_DOCS_URLS = {
  accessTokens: "https://expo.dev/accounts/[account]/settings/access-tokens",
  alternativeTokens: "https://expo.dev/settings/access-tokens",
  programmaticAccess: "https://docs.expo.dev/accounts/programmatic-access/"
} as const;

const MESSAGES = {
  errors: {
    emptyToken: "Please enter your Expo access token from your account settings",
    loadFailed: "Unable to load Expo integration settings. Please refresh and try again.",
    saveFailed: "Failed to save Expo integration. Please verify your token is valid and try again.",
    removeFailed: "Failed to disconnect Expo account. Please try again.",
    proPlanRequired: "Expo integration requires a Kolega Studio Pro plan. Upgrade to build mobile apps through our platform."
  },
  success: {
    saved: "Expo integration configured successfully! You can now create mobile apps through Kolega Studio.",
    updated: "Expo token updated successfully! Your connection remains active.",
    removed: "Expo integration removed. You'll need to reconnect your account to create mobile apps again."
  }
} as const;

// Alert Component
const Alert: React.FC<AlertProps> = ({ type, message, onDismiss }) => {
  const config = React.useMemo(() => {
    switch (type) {
      case 'success':
        return {
          icon: CheckCircle,
          bgClass: 'bg-green-50 dark:bg-green-900/20',
          borderClass: 'border-green-200 dark:border-green-800',
          textClass: 'text-green-800 dark:text-green-200',
          iconClass: 'text-green-600 dark:text-green-400'
        };
      case 'error':
        return {
          icon: AlertCircle,
          bgClass: 'bg-red-50 dark:bg-red-900/20',
          borderClass: 'border-red-200 dark:border-red-800',
          textClass: 'text-red-800 dark:text-red-200',
          iconClass: 'text-red-600 dark:text-red-400'
        };
      case 'warning':
        return {
          icon: AlertCircle,
          bgClass: 'bg-amber-50 dark:bg-amber-900/20',
          borderClass: 'border-amber-200 dark:border-amber-800',
          textClass: 'text-amber-800 dark:text-amber-200',
          iconClass: 'text-amber-600 dark:text-amber-400'
        };
      default:
        return {
          icon: Info,
          bgClass: 'bg-brand-primary/10 dark:bg-brand-primary/20',
          borderClass: 'border-brand-primary/30 dark:border-brand-primary/40',
          textClass: 'text-neutral-800 dark:text-neutral-200',
          iconClass: 'text-brand-primary'
        };
    }
  }, [type]);

  const Icon = config.icon;

  return (
    <div 
      className={`flex items-start gap-3 p-4 ${config.bgClass} border ${config.borderClass} rounded-lg transition-all duration-200 animate-in slide-in-from-top-1`}
      role="alert"
    >
      <Icon className={`w-5 h-5 ${config.iconClass} flex-shrink-0 mt-0.5`} />
      <span className={`text-sm ${config.textClass} flex-1`}>{message}</span>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className={`text-lg leading-none ${config.textClass} hover:opacity-70 transition-opacity`}
          aria-label="Dismiss"
        >
          ×
        </button>
      )}
    </div>
  );
};

// Loading Component
const LoadingSpinner: React.FC = () => (
  <div className="flex items-center gap-2">
    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-primary"></div>
    <span className="text-sm text-neutral-600 dark:text-neutral-400">Loading...</span>
  </div>
);

// Main Component
const ExpoIntegration: React.FC = () => {
  const { isReadOnly } = useReadOnlyMode();
  const api = useAuthenticatedApi();
  
  // State
  const [tokenData, setTokenData] = useState<ExpoIntegrationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<{ type: AlertProps['type']; message: string } | null>(null);
  const [tokenInput, setTokenInput] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  // Computed values
  const isConfigured = React.useMemo(() => tokenData?.is_active && tokenData?.has_token, [tokenData]);
  const canSave = React.useMemo(() => tokenInput.trim().length > 0 && !saving, [tokenInput, saving]);

  // Alert management
  const showAlert = useCallback((type: AlertProps['type'], message: string, duration?: number) => {
    setAlert({ type, message });
    if (duration) {
      setTimeout(() => setAlert(null), duration);
    }
  }, []);

  const dismissAlert = useCallback(() => setAlert(null), []);

  // API calls
  const fetchTokenData = useCallback(async () => {
    if (!api) return;
    
    try {
      setLoading(true);
      dismissAlert();
      const response = await api.get<ExpoIntegrationResponse>(apiEndpoints.integrations.expo.get);
      setTokenData(response);
      setTokenInput("");
    } catch (err: any) {
      console.error('Error fetching Expo integration:', err);
      const message = err.status === 403 
        ? MESSAGES.errors.proPlanRequired 
        : MESSAGES.errors.loadFailed;
      showAlert('error', message);
    } finally {
      setLoading(false);
    }
  }, [api, dismissAlert, showAlert]);

  const handleSave = useCallback(async () => {
    if (isReadOnly) return;

    const trimmedToken = tokenInput.trim();
    
    if (!trimmedToken) {
      showAlert('error', MESSAGES.errors.emptyToken, 5000);
      return;
    }

    try {
      setSaving(true);
      dismissAlert();
      
      await api.put(apiEndpoints.integrations.expo.update, { 
        expo_token: trimmedToken
      } as ExpoIntegrationRequest);
      
      const successMessage = isConfigured 
        ? MESSAGES.success.updated 
        : MESSAGES.success.saved;
      
      showAlert('success', successMessage, 5000);
      setIsEditing(false);
      setTokenInput("");
      await fetchTokenData();
      
      refreshTemplates();
      refreshWorkspaces();
    } catch (err: any) {
      console.error('Error saving Expo integration:', err);
      let message: string = MESSAGES.errors.saveFailed;
      
      if (err.status === 403) {
        message = MESSAGES.errors.proPlanRequired;
      } else if (err.status === 400 && err.data?.detail) {
        // Handle structured error responses from backend
        if (typeof err.data.detail === 'object' && err.data.detail.message) {
          message = err.data.detail.message;
        } else if (typeof err.data.detail === 'string') {
          message = err.data.detail;
        }
      } else if (err.data?.detail) {
        // Handle other specific error messages from backend
        if (typeof err.data.detail === 'object' && err.data.detail.message) {
          message = err.data.detail.message;
        } else if (typeof err.data.detail === 'string') {
          message = err.data.detail;
        }
      }
      
      showAlert('error', message);
    } finally {
      setSaving(false);
    }
  }, [api, tokenInput, isConfigured, fetchTokenData, showAlert, dismissAlert, isReadOnly]);

  const handleRemove = useCallback(async () => {
    if (isReadOnly) return;

    try {
      setSaving(true);
      dismissAlert();
      
      await api.delete(apiEndpoints.integrations.expo.remove);
      
      showAlert('success', MESSAGES.success.removed, 5000);
      setTokenInput("");
      setIsEditing(false);
      setConfirmingRemove(false);
      await fetchTokenData();
      
      refreshTemplates();
      refreshWorkspaces();
    } catch (err: any) {
      console.error('Error removing Expo integration:', err);
      const message = err.status === 403 
        ? MESSAGES.errors.proPlanRequired 
        : MESSAGES.errors.removeFailed;
      showAlert('error', message);
    } finally {
      setSaving(false);
    }
  }, [api, fetchTokenData, showAlert, dismissAlert, isReadOnly]);

  const handleCancel = useCallback(() => {
    setTokenInput("");
    setIsEditing(false);
    dismissAlert();
  }, [dismissAlert]);

  const handleStartEditing = useCallback(() => {
    setIsEditing(true);
    setConfirmingRemove(false);
    dismissAlert();
  }, [dismissAlert]);

  // Effects
  useEffect(() => {
    if (api) {
      fetchTokenData();
    }
  }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderTokenInput = () => (
    <div className="space-y-3 animate-in slide-in-from-bottom-2 duration-200">
      
      <label htmlFor="expo-token" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
        Authentication Token
      </label>
      
      <form onSubmit={(e) => {
        e.preventDefault();
        if (tokenInput.trim()) {
          handleSave();
        }
      }}>
        <div className="relative">
          <input
            id="expo-token"
            type="text"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Enter your Expo access token"
            autoComplete="off"
            autoFocus
            disabled={saving || isReadOnly}
            className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            title={isReadOnly ? 'Upgrade to add Expo token' : ''}
          />
        </div>
      </form>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <button
          onClick={handleSave}
          disabled={!canSave || isReadOnly}
          className={`px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
            isReadOnly
              ? 'bg-neutral-300 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400'
              : 'bg-brand-primary hover:bg-brand-primaryDark text-black'
          }`}
          title={isReadOnly ? 'Upgrade to save token' : ''}
        >
          {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>}
          {isConfigured ? 'Update Token' : 'Save Token'}
        </button>
        
        <button
          onClick={handleCancel}
          disabled={saving}
          className="px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  const renderConfiguredState = () => {
    const formatDate = (dateStr: string | null) => {
      if (!dateStr) return 'Unknown';
      try {
        return new Date(dateStr).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch {
        return 'Unknown';
      }
    };

    return (
      <div className="space-y-4">
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 space-y-2">
              <span className="text-sm text-green-800 dark:text-green-200 font-medium block">
              Expo account connected and linked successfully.
              </span>
              
              {/* Expo Account Details */}
              {(tokenData?.expo_username || tokenData?.expo_email) && (
                <div className="space-y-1 text-xs text-green-700 dark:text-green-300">
                  {tokenData.expo_username && (
                    <div className="flex items-center gap-1">
                      <span className="font-medium">Account:</span>
                      <span>{tokenData.expo_username}</span>
                    </div>
                  )}
                  {tokenData.expo_email && (
                    <div className="flex items-center gap-1">
                      <span className="font-medium">Email:</span>
                      <span>{tokenData.expo_email}</span>
                    </div>
                  )}
                  {tokenData.last_validated_at && (
                    <div className="flex items-center gap-1">
                      <span className="font-medium">Last validated:</span>
                      <span>{formatDate(tokenData.last_validated_at)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {confirmingRemove ? (
        <div className="space-y-3">
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Are you sure you want to disconnect your Expo account? You won't be able to create new Expo projects or access existing Expo-based projects until you reconnect. Your projects on Expo will not be affected.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button
              onClick={handleRemove}
              disabled={saving}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
              Yes, Remove Token
            </button>
            
            <button
              onClick={() => setConfirmingRemove(false)}
              disabled={saving}
              className="px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <button
            onClick={handleStartEditing}
            disabled={saving || isReadOnly}
            title={isReadOnly ? 'Upgrade to update token' : ''}
            className="px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Update Token
          </button>
          
          <button
            onClick={() => setConfirmingRemove(true)}
            disabled={saving || isReadOnly}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            title={isReadOnly ? 'Upgrade to remove token' : ''}
          >
            {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
            Remove Token
          </button>
        </div>
      )}
    </div>
  );
};

  const renderUnconfiguredState = () => (
    <div className="space-y-3">
      <button
        onClick={handleStartEditing}
        disabled={saving || isReadOnly}
        className={`px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
          isReadOnly
            ? 'bg-neutral-300 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400'
            : 'bg-brand-primary hover:bg-brand-primaryDark text-black'
        }`}
        title={isReadOnly ? 'Upgrade to add Expo token' : ''}
      >
        Add Expo Token
      </button>
    </div>
  );

  // Main render
  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-4">
      {/* Description text - always visible */}
      <p className="text-sm text-neutral-600 dark:text-neutral-400">
        You'll need an Expo account to create, build, and deploy mobile apps with Kolega Studio.
        <br />
        <strong className="text-neutral-700 dark:text-neutral-300">
          Don't have an account?
        </strong>{' '}
        <a
          href="https://expo.dev/signup"
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-primary hover:text-brand-primaryDark inline-flex items-center gap-1"
        >
          Sign up for free.
          <ExternalLink className="w-3 h-3" />
        </a>
        <br />
        Only <strong className="text-neutral-700 dark:text-neutral-300">Personal Access Tokens</strong> are supported. Generated via the Access Tokens menu.
        <br />
        For more information, see the{' '}
        <a
          href={EXPO_DOCS_URLS.programmaticAccess}
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-primary hover:text-brand-primaryDark inline-flex items-center gap-1"
        >
          programmatic access docs
          <ExternalLink className="w-3 h-3" />
        </a>
      </p>

      {/* Alert */}
      {alert && (
        <Alert 
          type={alert.type} 
          message={alert.message} 
          onDismiss={dismissAlert}
        />
      )}

      {/* Main Content */}
      {isEditing ? (
        renderTokenInput()
      ) : isConfigured ? (
        renderConfiguredState()
      ) : (
        renderUnconfiguredState()
      )}
    </div>
  );
};

export default ExpoIntegration;